import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { supabase } from '@/lib/supabase';
import {
  Check,
  CheckCircle2,
  ArrowLeft,
  Clock,
  User,
  FileText,
  File,
  Link2,
  ChevronRight,
  Download,
  Upload,
  Eye,
  AlertCircle,
  Hand,
  Edit,
  ExternalLink,
  Plus,
  Trash2,
  X,
  Monitor,
  Smartphone,
  PenLine,
  RotateCcw,
  Zap,
  FlagTriangleRight,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { triggerFlow } from '@/lib/aiFlowsService';
import FlowExecutionScreen from './FlowExecutionScreen';

/**
 * Format a form field value for display in variable replacement
 */
function formatFieldValueForDisplay(value, fieldType) {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    if (fieldType === 'Table') {
      return `${value.length} row${value.length !== 1 ? 's' : ''}`;
    }
    return value.join(', ');
  }

  if (typeof value === 'object') {
    if (value.first_name || value.last_name) {
      return `${value.first_name || ''} ${value.last_name || ''}`.trim();
    }
    if (value.name && value.url) {
      return value.name;
    }
    return JSON.stringify(value);
  }

  if (typeof value === 'string' && value.startsWith('data:image')) {
    return '[Signature]';
  }

  if (fieldType === 'Date' && value) {
    try {
      return new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return value;
    }
  }

  return String(value);
}

/**
 * Flatten a flow result object into dot-notation keys for variable replacement.
 * e.g. { files: ["url"] } → { "flow.files": "url", "flow.files.0": "url" }
 */
function flattenFlowResult(result) {
  const flattened = {};
  if (!result || typeof result !== 'object') return flattened;

  function flatten(obj, prefix) {
    if (obj === null || obj === undefined) return;
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
      if (obj.length > 0) flattened[prefix] = String(obj[0]);
      return;
    }
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
      return;
    }
    flattened[prefix] = String(obj);
  }

  flatten(result, 'flow');

  // Add "flow.result.*" aliases so {{ flow.result.files.0 }} also works
  const aliases = {};
  for (const [key, value] of Object.entries(flattened)) {
    if (key.startsWith('flow.')) {
      aliases[`flow.result.${key.slice(5)}`] = value;
    }
  }
  Object.assign(flattened, aliases);

  return flattened;
}

/**
 * Convert a field name to a URL-safe slug for use as payload keys.
 */
function toSlug(name) {
  if (!name) return '';
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Transform form_data from raw field IDs to friendly slugified names.
 */
function buildFriendlyFormData(formData, workflowTasks) {
  if (!formData || !workflowTasks) return formData || {};

  const idToSlug = {};
  const slugCounts = {};

  workflowTasks.forEach(task => {
    (task.content || [])
      .filter(item => item.category === 'form')
      .forEach(item => {
        const name = item.content?.fieldName || item.label || '';
        let slug = toSlug(name);
        if (!slug) slug = item.id;
        if (slugCounts[slug]) {
          slugCounts[slug]++;
          idToSlug[item.id] = `${slug}_${slugCounts[slug]}`;
        } else {
          slugCounts[slug] = 1;
          idToSlug[item.id] = slug;
        }
      });
  });

  const friendly = {};
  for (const [key, value] of Object.entries(formData)) {
    friendly[idToSlug[key] || key] = value;
  }
  return friendly;
}

/**
 * Replace variable placeholders in HTML content with form data values.
 * Supports both raw field IDs ({{ input-xxx }}) and friendly slugs ({{ first_name }}).
 */
function replaceVariablesInContent(htmlContent, formData, allFields) {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent || '';
  }

  // Build slug → rawID mapping for resolving friendly variable names
  let slugToId = null;
  const getSlugMap = () => {
    if (slugToId) return slugToId;
    slugToId = {};
    if (allFields) {
      const slugCounts = {};
      for (const [id, item] of Object.entries(allFields)) {
        const name = item.content?.fieldName || item.label || '';
        let slug = toSlug(name);
        if (!slug) continue;
        if (slugCounts[slug]) {
          slugCounts[slug]++;
          slug = `${slug}_${slugCounts[slug]}`;
        } else {
          slugCounts[slug] = 1;
        }
        slugToId[slug] = id;
      }
    }
    return slugToId;
  };

  return htmlContent.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, key) => {
    const trimmedKey = key.trim();

    // Normalize bracket notation to dot notation: flow.files[0] → flow.files.0
    const normalizedKey = trimmedKey.replace(/\[(\d+)\]/g, '.$1');

    // Try direct lookup by raw ID or normalized key
    let lookupKey = formData?.[trimmedKey] !== undefined ? trimmedKey
      : formData?.[normalizedKey] !== undefined ? normalizedKey
      : null;

    // If not found, try resolving as a friendly slug → raw ID
    if (lookupKey === null) {
      const map = getSlugMap();
      const rawId = map[trimmedKey];
      if (rawId && formData?.[rawId] !== undefined) {
        lookupKey = rawId;
      }
    }

    if (lookupKey !== null) {
      const fieldInfo = allFields?.[lookupKey];
      const fieldType = fieldInfo?.label || '';
      const value = formatFieldValueForDisplay(formData[lookupKey], fieldType);
      return value || `<span class="text-muted-foreground italic">[Not provided]</span>`;
    }

    if (trimmedKey.startsWith('current_date.') || trimmedKey === 'current_time') {
      const now = new Date();
      switch (trimmedKey) {
        case 'current_date.full':
          return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        case 'current_date.short':
          return now.toLocaleDateString('en-GB');
        case 'current_date.iso':
          return now.toISOString().split('T')[0];
        case 'current_time':
          return now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        default:
          return `[${trimmedKey}]`;
      }
    }

    return `<span class="text-muted-foreground italic">[${trimmedKey}]</span>`;
  });
}

/**
 * SignatureCapture Component for preview
 */
function SignatureCapture({ value, onChange, fieldName, renderLabel }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      onChange(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    onChange(null);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
    }
  }, []);

  return (
    <div className="space-y-2">
      {renderLabel(fieldName)}
      <div className="border-2 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full h-[150px] cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <PenLine className="h-3 w-3" />
          <span>Draw your signature above</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="h-7 text-xs"
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
}

// Dynamic Dropdown Component for preview - fetches options from API or AiTables
function DynamicDropdown({ item, value, onChange, renderLabel, fieldName, companyId }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dataSource = item.content?.dataSource || {};
  const aiTablesConfig = item.content?.aiTablesConfig || {};
  const dataSourceType = item.content?.dataSourceType || 'static';

  useEffect(() => {
    // Static options - use stored options directly
    if (dataSourceType === 'static') {
      setOptions(item.content?.options || []);
      return;
    }

    // Dynamic API fetch
    if (dataSourceType === 'dynamic') {
      if (!dataSource.url) {
        setOptions([]);
        return;
      }

      const fetchOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(dataSource.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch options: ${response.status}`);
          }
          const data = await response.json();

          // Navigate to data path if specified
          let items = data;
          if (dataSource.dataPath) {
            const pathParts = dataSource.dataPath.split('.');
            for (const part of pathParts) {
              items = items?.[part];
            }
          }

          if (!Array.isArray(items)) {
            throw new Error('Data source did not return an array');
          }

          // Map to option format
          const valueField = dataSource.valueField || 'id';
          const labelField = dataSource.labelField || 'name';
          const mappedOptions = items.map((item, idx) => ({
            id: item[valueField] || idx,
            value: String(item[valueField] || idx),
            label: String(item[labelField] || item[valueField] || idx)
          }));

          setOptions(mappedOptions);
        } catch (err) {
          console.error('Error fetching dropdown options:', err);
          setError(err.message);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();
      return;
    }

    // AiTables fetch
    if (dataSourceType === 'aitables') {
      if (!companyId || !aiTablesConfig.tableId) {
        setError('AiTables configuration incomplete');
        setOptions([]);
        return;
      }

      const fetchAiTablesOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const { getDropdownOptions } = await import('@/lib/aitablesService');
          const mappedOptions = await getDropdownOptions(
            companyId,
            aiTablesConfig.tableId,
            aiTablesConfig.valueColumn || 'id',
            aiTablesConfig.labelColumn || 'name'
          );
          setOptions(mappedOptions);
        } catch (err) {
          console.error('Error fetching AiTables dropdown options:', err);
          setError(err.message || 'Failed to fetch options from AiTables');
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      fetchAiTablesOptions();
      return;
    }

    // Fallback for unknown types
    setOptions(item.content?.options || []);
  }, [dataSourceType, dataSource.url, dataSource.dataPath, dataSource.valueField, dataSource.labelField, item.content?.options, companyId, aiTablesConfig.tableId, aiTablesConfig.valueColumn, aiTablesConfig.labelColumn]);

  return (
    <div className="space-y-2">
      {renderLabel(fieldName)}
      {loading ? (
        <div className="h-10 flex items-center justify-center border rounded-md bg-muted/50">
          <span className="text-sm text-muted-foreground">Loading options...</span>
        </div>
      ) : error ? (
        <div className="p-2 border border-destructive/50 rounded-md bg-destructive/10">
          <p className="text-xs text-destructive">{error}</p>
        </div>
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.value}>
                {option.label || option.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export default function WorkflowPreviewPage() {
  const navigate = useNavigate();
  const { projectId, workflowId } = useParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Workflow Preview');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [workflow, setWorkflow] = useState(null);
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [previewFormData, setPreviewFormData] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFlowExecuting, setIsFlowExecuting] = useState(false);
  const [flowExecutionMessage, setFlowExecutionMessage] = useState('Running automation...');
  const [flowResult, setFlowResult] = useState(null);
  const [flowOutputData, setFlowOutputData] = useState({});
  const flowExecutionRef = useRef(null);
  const [previewWebhookToken, setPreviewWebhookToken] = useState(null);

  const SUPABASE_URL = 'https://yqmlqctpakqxtwzrdofl.supabase.co';

  // Update time for mobile preview
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Load workflow data
  useEffect(() => {
    const loadWorkflow = async () => {
      if (!workflowId) return;

      try {
        const { data, error } = await supabase
          .from('workflows')
          .select('*')
          .eq('id', workflowId)
          .single();

        if (error) throw error;
        setWorkflow(data);
      } catch (error) {
        console.error('Error loading workflow:', error);
        toast.error('Failed to load workflow');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflow();
  }, [workflowId]);

  // Load team members for User field selection
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!projectId) return;

      try {
        // First try to get project team members
        const { data: teamData, error: teamError } = await supabase
          .from('team_members')
          .select(`
            user_id,
            user_profiles!inner(
              id,
              first_name,
              last_name,
              avatar_url,
              email
            )
          `)
          .eq('project_id', projectId);

        if (teamError) throw teamError;

        if (teamData && teamData.length > 0) {
          const users = teamData.map(member => ({
            id: member.user_profiles.id,
            first_name: member.user_profiles.first_name,
            last_name: member.user_profiles.last_name,
            avatar_url: member.user_profiles.avatar_url,
            email: member.user_profiles.email,
          }));
          setTeamMembers(users);
        } else {
          // Fall back to company members if no project team members
          const { data: projectData } = await supabase
            .from('projects')
            .select('company_id')
            .eq('id', projectId)
            .single();

          if (projectData?.company_id) {
            const { data: companyData } = await supabase
              .from('company_members')
              .select(`
                user_id,
                user_profiles!inner(
                  id,
                  first_name,
                  last_name,
                  avatar_url,
                  email
                )
              `)
              .eq('company_id', projectData.company_id);

            if (companyData) {
              const users = companyData.map(member => ({
                id: member.user_profiles.id,
                first_name: member.user_profiles.first_name,
                last_name: member.user_profiles.last_name,
                avatar_url: member.user_profiles.avatar_url,
                email: member.user_profiles.email,
              }));
              setTeamMembers(users);
            }
          }
        }
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };

    loadTeamMembers();
  }, [projectId]);

  const tasks = workflow?.tasks || [];
  const activeTask = tasks[activeTaskIndex];

  // Execute AI Flow when a flow node becomes active
  useEffect(() => {
    if (!activeTask || activeTask.type !== 'flow') return;
    if (!company?.id) return;

    // Prevent double-trigger for the same task index
    if (flowExecutionRef.current === activeTaskIndex) return;
    flowExecutionRef.current = activeTaskIndex;

    const aiFlowConfig = activeTask.aiFlowConfig;
    if (!aiFlowConfig?.enabled || !aiFlowConfig?.flowId) {
      // No flow configured - auto-advance
      toast.info('Flow node has no flow configured, skipping');
      if (activeTaskIndex < tasks.length - 1) {
        setActiveTaskIndex(prev => prev + 1);
      }
      return;
    }

    const mustWait = aiFlowConfig.mustWait ?? false;

    const executeFlow = async () => {
      setIsFlowExecuting(true);
      setFlowExecutionMessage(aiFlowConfig.waitMessage || 'Running automation...');
      setFlowResult(null);

      try {
        // Generate a test webhook token so the external system can POST back
        const token = crypto.randomUUID();
        setPreviewWebhookToken(token);

        // Build payload with friendly slugified field names and test webhook URL
        const friendlyData = buildFriendlyFormData(previewFormData, tasks);
        const payload = {
          workflow_run_id: `preview-${Date.now()}`,
          task_run_id: `preview-task-${activeTaskIndex}`,
          form_data: friendlyData,
          workflow_name: workflow?.name || 'Untitled Workflow',
          task_name: activeTask.name || activeTask.formTitle || 'Flow Step',
          webhook_url: `${SUPABASE_URL}/functions/v1/process-webhook/test/${token}`,
        };

        // Fire-and-forget: trigger the flow, don't wait for completion
        const result = await triggerFlow(
          company.id,
          aiFlowConfig.flowId,
          payload,
          false
        );

        console.log('[Preview Flow] triggerFlow result:', result);

        if (!mustWait) {
          // Non-blocking: flatten any immediate output and auto-advance
          const flatData = flattenFlowResult(result.result);
          setFlowOutputData(prev => ({ ...prev, ...flatData }));
          setFlowResult(result);
          setFlowExecutionMessage('Automation triggered!');
          setPreviewWebhookToken(null); // No need to listen
          setTimeout(() => {
            setIsFlowExecuting(false);
            flowExecutionRef.current = null;
            if (activeTaskIndex < tasks.length - 1) {
              setActiveTaskIndex(prev => prev + 1);
            }
          }, 1000);
        } else {
          // mustWait: flow triggered, now waiting for webhook callback via Realtime
          setFlowExecutionMessage('Waiting for automation to complete...');
          // The Realtime subscription below will handle the callback
        }
      } catch (error) {
        console.error('Preview flow execution error:', error);
        setFlowResult({ error: error.message });
        setFlowExecutionMessage('Automation failed');
        toast.error('Flow execution failed: ' + error.message);
        setPreviewWebhookToken(null);
        flowExecutionRef.current = null;
      }
    };

    executeFlow();
  }, [activeTaskIndex, activeTask?.type, company?.id]);

  // Realtime broadcast subscription for preview webhook callback
  useEffect(() => {
    if (!isFlowExecuting || !previewWebhookToken) return;

    const channel = supabase
      .channel(`webhook-test-${previewWebhookToken}`)
      .on('broadcast', { event: 'webhook_callback' }, (payload) => {
        console.log('[Preview Webhook] Broadcast received:', payload);
        const webhookData = payload.payload?.data;
        if (webhookData) {
          // Flatten the webhook data into flow output variables
          const flatData = {};
          const flatten = (obj, prefix) => {
            if (obj === null || obj === undefined) return;
            if (Array.isArray(obj)) {
              obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
              if (obj.length > 0) flatData[prefix] = String(obj[0]);
              return;
            }
            if (typeof obj === 'object') {
              Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
              return;
            }
            flatData[prefix] = String(obj);
          };
          flatten(webhookData, 'webhook');

          setFlowOutputData(prev => ({ ...prev, ...flatData }));
          setFlowResult({ success: true, webhookData });
          setFlowExecutionMessage('Automation complete!');
          setPreviewWebhookToken(null);

          // Auto-advance after showing success
          setTimeout(() => {
            setIsFlowExecuting(false);
            flowExecutionRef.current = null;
            if (activeTaskIndex < tasks.length - 1) {
              setActiveTaskIndex(prev => prev + 1);
            }
          }, 1500);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isFlowExecuting, previewWebhookToken, activeTaskIndex, tasks.length]);

  // Build allFields map for variable replacement
  const allFields = React.useMemo(() => {
    const fields = {};
    tasks.forEach(task => {
      (task.content || []).forEach(item => {
        if (item.category === 'form') {
          fields[item.id] = item;
        }
      });
    });
    return fields;
  }, [tasks]);

  // Navigate to a specific task, resetting flow state if needed
  const navigateToTask = (index) => {
    if (isFlowExecuting) {
      setIsFlowExecuting(false);
      setFlowResult(null);
      setPreviewWebhookToken(null); // Stop listening for webhook
      flowExecutionRef.current = null;
    }
    setActiveTaskIndex(index);
  };

  // Handle preview form changes (just for visual feedback, not saved)
  const handlePreviewFormChange = (fieldId, value) => {
    setPreviewFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Handle subtask toggle for preview
  const handleSubtaskToggle = (fieldId, subtaskId, completed) => {
    const currentSubtasks = previewFormData[fieldId] || {};
    const updatedSubtasks = {
      ...currentSubtasks,
      [subtaskId]: completed,
    };
    handlePreviewFormChange(fieldId, updatedSubtasks);
  };

  // Calculate progress (in preview, simulate based on active task)
  const progressPercent = tasks.length > 0 ? (activeTaskIndex / tasks.length) * 100 : 0;

  // Render form field based on type - matching WorkflowRunPage exactly
  const renderFormField = (item) => {
    const fieldId = item.id;
    const value = previewFormData[fieldId] || '';
    const fieldName = item.content?.fieldName || item.label;
    const isRequired = item.content?.required;

    // Helper to render label with variable replacement
    const renderLabel = (name) => {
      const processedName = replaceVariablesInContent(name, { ...previewFormData, ...flowOutputData }, allFields);
      return (
        <label className="text-sm font-medium">
          <span dangerouslySetInnerHTML={{ __html: processedName }} />
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      );
    };

    switch (item.label) {
      case 'Short Text':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
            />
          </div>
        );

      case 'Long Text':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Textarea
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
              rows={4}
            />
          </div>
        );

      case 'Email':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="email"
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder="Enter email address"
            />
          </div>
        );

      case 'Website':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="url"
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder="https://..."
            />
          </div>
        );

      case 'Numbers':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="number"
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder="Enter number"
            />
          </div>
        );

      case 'Date':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="date"
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
            />
          </div>
        );

      case 'User':
        const selectedUser = value ? (typeof value === 'string' ? { id: value } : value) : null;
        const selectedUserData = selectedUser?.id ? teamMembers.find(u => u.id === selectedUser.id) : null;

        const searchTeamMembers = async (query) => {
          const filtered = teamMembers.filter(user => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            const email = (user.email || '').toLowerCase();
            const searchTerm = query.toLowerCase();
            return fullName.includes(searchTerm) || email.includes(searchTerm);
          });

          return filtered.map(user => ({
            value: user.id,
            label: `${user.first_name} ${user.last_name}`,
            data: user,
          }));
        };

        const getDisplayLabel = () => {
          if (selectedUser?.first_name && selectedUser?.last_name) {
            return `${selectedUser.first_name} ${selectedUser.last_name}`;
          }
          if (selectedUserData) {
            return `${selectedUserData.first_name} ${selectedUserData.last_name}`;
          }
          return 'Select a team member...';
        };

        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <AsyncCombobox
              value={selectedUser?.id || ''}
              onValueChange={(newValue) => {
                if (!newValue) {
                  handlePreviewFormChange(fieldId, null);
                }
              }}
              onSelectionChange={(userData) => {
                handlePreviewFormChange(fieldId, {
                  id: userData.id,
                  first_name: userData.first_name,
                  last_name: userData.last_name,
                  email: userData.email,
                  avatar_url: userData.avatar_url,
                });
              }}
              onSearch={searchTeamMembers}
              placeholder="Select a team member..."
              emptyText="No team members found"
              displayLabel={getDisplayLabel()}
            />
            {selectedUser?.id && selectedUserData && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={selectedUserData.avatar_url} alt={`${selectedUserData.first_name} ${selectedUserData.last_name}`} />
                  <AvatarFallback>
                    {selectedUserData.first_name?.[0]}{selectedUserData.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{selectedUserData.first_name} {selectedUserData.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUserData.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreviewFormChange(fieldId, null)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        );

      case 'Dropdown':
        return (
          <DynamicDropdown
            item={item}
            value={value}
            onChange={(val) => handlePreviewFormChange(fieldId, val)}
            renderLabel={renderLabel}
            fieldName={fieldName}
            companyId={company?.id}
          />
        );

      case 'Multi Choice':
        const multiOptions = item.content?.options || [];
        const selectedValues = value ? (Array.isArray(value) ? value : [value]) : [];
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <div className="space-y-2">
              {multiOptions.map((option) => (
                <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={(checked) => {
                      const newValues = checked
                        ? [...selectedValues, option.value]
                        : selectedValues.filter(v => v !== option.value);
                      handlePreviewFormChange(fieldId, newValues);
                    }}
                  />
                  <span>{option.value}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'File Upload':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <label className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors block">
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload a file
                </span>
                <span className="text-xs text-muted-foreground">
                  Max 50MB
                </span>
              </div>
            </label>
          </div>
        );

      case 'Table':
        const tableColumns = item.content?.columns || [];
        const tableRows = value || [{ id: 'preview-row-1' }];

        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            {tableColumns.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      {tableColumns.map(col => (
                        <th key={col.id} className="px-3 py-2 text-left text-sm font-medium border-b">
                          {col.name || 'Column'}
                        </th>
                      ))}
                      <th className="w-10 border-b"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, rowIndex) => (
                      <tr key={row.id || rowIndex} className="border-b last:border-b-0">
                        {tableColumns.map(col => (
                          <td key={col.id} className="p-1">
                            <Input
                              className="h-8 text-sm border-0 focus-visible:ring-1"
                              placeholder="Enter value..."
                            />
                          </td>
                        ))}
                        <td className="p-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 bg-muted/30 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Row
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 border rounded-lg text-center text-sm text-muted-foreground">
                No columns configured for this table
              </div>
            )}
          </div>
        );

      case 'Signature':
        return (
          <SignatureCapture
            value={value}
            onChange={(newValue) => handlePreviewFormChange(fieldId, newValue)}
            fieldName={fieldName}
            renderLabel={renderLabel}
          />
        );

      default:
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              value={value}
              onChange={(e) => handlePreviewFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
            />
          </div>
        );
    }
  };

  // Render content item (non-form items like text, images, etc.) - matching WorkflowRunPage
  const renderContentItem = (item) => {
    switch (item.label) {
      case 'Text': {
        // Replace variables like {{ fieldId }} with actual form data values
        const processedContent = replaceVariablesInContent(
          item.content,
          { ...previewFormData, ...flowOutputData },
          allFields
        );
        return (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        );
      }

      case 'Image':
        const imageDataRaw = typeof item.content === 'string'
          ? { url: item.content }
          : item.content;
        const resolvedImageUrl = replaceVariablesInContent(imageDataRaw?.url || '', { ...previewFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        return resolvedImageUrl ? (
          <div className="space-y-2">
            <img
              src={resolvedImageUrl}
              alt={imageDataRaw?.description || 'Image'}
              className="w-full rounded-lg"
            />
            {imageDataRaw?.description && (
              <p className="text-sm text-muted-foreground italic">{imageDataRaw.description}</p>
            )}
          </div>
        ) : null;

      case 'Video':
        const videoDataRaw = typeof item.content === 'string'
          ? { url: item.content }
          : item.content;
        const resolvedVideoUrl = replaceVariablesInContent(videoDataRaw?.url || '', { ...previewFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        if (!resolvedVideoUrl) return null;

        const isEmbed = resolvedVideoUrl.includes('youtube.com') || resolvedVideoUrl.includes('vimeo.com');
        return (
          <div className="space-y-2">
            {isEmbed ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={resolvedVideoUrl}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            ) : (
              <video src={resolvedVideoUrl} controls className="w-full rounded-lg" />
            )}
            {videoDataRaw?.description && (
              <p className="text-sm text-muted-foreground italic">{videoDataRaw.description}</p>
            )}
          </div>
        );

      case 'File':
        const fileDataRaw = typeof item.content === 'string'
          ? { url: item.content, name: 'Download' }
          : item.content;

        // Resolve variables in file URL and name
        const mergedVarData = { ...previewFormData, ...flowOutputData };
        const resolvedFileUrl = replaceVariablesInContent(fileDataRaw?.url || '', mergedVarData, allFields)
          .replace(/<[^>]+>/g, '');
        const resolvedFileName = replaceVariablesInContent(fileDataRaw?.name || 'File', mergedVarData, allFields)
          .replace(/<[^>]+>/g, '');
        const fileData = { ...fileDataRaw, url: resolvedFileUrl, name: resolvedFileName };

        return fileData?.url ? (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <File className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">{fileData.name || 'File'}</p>
                {fileData.description && (
                  <p className="text-sm text-muted-foreground">{fileData.description}</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(fileData.url, '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        ) : null;

      case 'Subtasks':
        const subtasks = item.content || [];
        const subtaskStates = previewFormData[item.id] || {};
        return (
          <div className="space-y-2">
            {subtasks.map((subtask) => (
              <label key={subtask.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer">
                <Checkbox
                  checked={subtaskStates[subtask.id] || false}
                  onCheckedChange={(checked) => handleSubtaskToggle(item.id, subtask.id, checked)}
                />
                <span className={subtaskStates[subtask.id] ? 'line-through text-muted-foreground' : ''}>
                  {subtask.text}
                </span>
              </label>
            ))}
          </div>
        );

      case 'Page':
        const pageData = item.content;
        if (!pageData) return null;
        return (
          <div className="border rounded-lg p-4 bg-muted/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{pageData.name}</div>
                <div className="text-xs text-muted-foreground">
                  Referenced page
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/page/${pageData.id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Page
              </Button>
            </div>
          </div>
        );

      case 'Embed':
        const embedDataRaw = item.content;
        const resolvedEmbedUrl = replaceVariablesInContent(embedDataRaw?.url || '', { ...previewFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        if (!resolvedEmbedUrl) return null;
        const isPdfEmbed = /\.pdf(\?|$|#)/i.test(resolvedEmbedUrl);
        return (
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm text-muted-foreground">
              <Link2 className="h-4 w-4" />
              <span className="truncate">{resolvedEmbedUrl}</span>
            </div>
            <div className="relative w-full h-[400px] bg-white">
              <iframe
                src={resolvedEmbedUrl}
                className="w-full h-full border-0"
                title="Embedded content"
                {...(!isPdfEmbed && { sandbox: 'allow-same-origin allow-scripts allow-popups allow-forms' })}
                loading="lazy"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || isLoading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (!workflow) {
    return (
      <AppShell3>
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground" />
          <p className="text-lg text-muted-foreground">Workflow not found</p>
          <Button onClick={() => navigate(`/space/${projectId}/tasks`)}>
            Back to Tasks
          </Button>
        </div>
      </AppShell3>
    );
  }

  // Format time for mobile preview
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <AppShell3 pageTitle="" pageDescription="">
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          spaceImage={space?.logo_url}
          toolName={workflow.name}
          currentTool="tasks"
          spaceTools={spaceTools}
          parentBreadcrumb={{
            name: 'Tasks',
            onClick: () => navigate(`/space/${projectId}/tasks`)
          }}
        />

        {/* Preview Banner - below subnav */}
        <div className="bg-blue-500 text-white py-2 px-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span className="font-medium">Preview Mode</span>
              <span className="text-blue-100 hidden sm:inline">- This is how your workflow will appear to users</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Device Toggle */}
              <div className="flex items-center bg-blue-600 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'desktop'
                      ? 'bg-white text-blue-600'
                      : 'text-blue-100 hover:text-white'
                  }`}
                  title="Desktop view"
                >
                  <Monitor className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'mobile'
                      ? 'bg-white text-blue-600'
                      : 'text-blue-100 hover:text-white'
                  }`}
                  title="Mobile view"
                >
                  <Smartphone className="h-4 w-4" />
                </button>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/workflow-editor?id=${workflowId}`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Workflow
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/tasks`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          {viewMode === 'desktop' ? (
            <>
              {/* Progress Bar - matching WorkflowRunPage */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {activeTaskIndex} of {tasks.length} tasks completed
                  </span>
                  <span className="text-sm font-medium">{Math.round(progressPercent)}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>

              <div className="flex gap-6">
                {/* Left Sidebar - Task List - matching WorkflowRunPage */}
                <div className="w-80 border rounded-lg bg-card flex flex-col overflow-y-auto sticky top-6 h-[calc(100vh-200px)]">
                  {/* Cover Image */}
                  {workflow.settings?.notificationCover && (
                    <div className="relative">
                      {workflow.settings.notificationCover.type === 'image' ? (
                        <img
                          src={workflow.settings.notificationCover.value}
                          alt="Workflow cover"
                          className="w-full h-32 object-cover rounded-t-lg"
                        />
                      ) : (
                        <div
                          className="w-full h-32 rounded-t-lg"
                          style={{ background: workflow.settings.notificationCover.value }}
                        />
                      )}
                    </div>
                  )}

                  <div className="p-4 flex-1">
                    <div className="space-y-1">
                      {tasks.map((task, index) => {
                        const isActive = index === activeTaskIndex;
                        const isCompleted = index < activeTaskIndex;
                        const isFlowNode = task.type === 'flow';
                        const isEndNode = task.type === 'end';

                        return (
                          <button
                            key={task.id}
                            onClick={() => navigateToTask(index)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                              isActive
                                ? isFlowNode ? 'bg-blue-50 border border-blue-400'
                                : isEndNode ? 'bg-emerald-50 border border-emerald-400'
                                : 'bg-primary/10 border border-primary'
                                : 'hover:bg-muted'
                            }`}
                          >
                            {/* Status Icon */}
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                              isCompleted
                                ? isFlowNode ? 'bg-blue-500 text-white' : isEndNode ? 'bg-emerald-500 text-white' : 'bg-green-500 text-white'
                                : isActive
                                ? isFlowNode ? 'bg-blue-500 text-white' : isEndNode ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground'
                                : isFlowNode ? 'border-2 border-blue-400 text-blue-400' : isEndNode ? 'border-2 border-emerald-400 text-emerald-400' : 'border-2 border-muted-foreground'
                            }`}>
                              {isCompleted ? (
                                isFlowNode ? <Zap className="h-3 w-3" /> : isEndNode ? <FlagTriangleRight className="h-3 w-3" /> : <Check className="h-4 w-4" />
                              ) : isFlowNode ? (
                                <Zap className="h-3 w-3" />
                              ) : isEndNode ? (
                                <FlagTriangleRight className="h-3 w-3" />
                              ) : (
                                <span className="text-xs font-medium">{index + 1}</span>
                              )}
                            </div>

                            {/* Task Name */}
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium truncate ${isCompleted ? 'text-muted-foreground line-through' : ''}`}>
                                {task.name || task.formTitle || `Task ${index + 1}`}
                              </div>
                              {task.dueDate && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  Due date configured
                                </div>
                              )}
                            </div>

                            {isActive && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Main Content - Active Task */}
                <div className="flex-1">
                  {/* Flow Execution Loading Screen */}
                  {isFlowExecuting ? (
                    <div
                      className={`rounded-lg p-6 min-h-[500px] ${
                        !workflow.settings?.background
                          ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200'
                          : ''
                      }`}
                      style={
                        workflow.settings?.background
                          ? workflow.settings.background.type === 'image'
                            ? { backgroundImage: `url(${workflow.settings.background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                            : { background: workflow.settings.background.value }
                          : {}
                      }
                    >
                      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto p-8 min-h-[400px] flex flex-col items-center justify-center">
                        <FlowExecutionScreen
                          isComplete={!!flowResult && !flowResult.error}
                          error={flowResult?.error || null}
                          customMessage={flowExecutionMessage !== 'Running automation...' ? flowExecutionMessage : null}
                          onRetry={() => {
                            setFlowResult(null);
                            flowExecutionRef.current = null;
                            setIsFlowExecuting(false);
                            setActiveTaskIndex(() => {
                              setTimeout(() => setActiveTaskIndex(activeTaskIndex), 0);
                              return -1;
                            });
                          }}
                          onSkip={() => {
                            if (activeTaskIndex < tasks.length - 1) {
                              navigateToTask(activeTaskIndex + 1);
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : activeTask ? (
                    <div
                      className={`rounded-lg p-6 min-h-[500px] ${
                        !workflow.settings?.background
                          ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200'
                          : ''
                      }`}
                      style={
                        workflow.settings?.background
                          ? workflow.settings.background.type === 'image'
                            ? { backgroundImage: `url(${workflow.settings.background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                            : { background: workflow.settings.background.value }
                          : {}
                      }
                    >
                      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto p-8">
                        {/* End Screen Badge */}
                        {activeTask.type === 'end' && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                              <FlagTriangleRight className="h-3 w-3" />
                              End Screen
                            </div>
                          </div>
                        )}

                        {/* Task Header */}
                        <h2 className="text-2xl font-bold mb-6">
                          {activeTask.formTitle || activeTask.name || 'Untitled Task'}
                        </h2>

                        <div className={`border-t mb-6 ${activeTask.type === 'end' ? 'border-emerald-200' : ''}`} />

                        {/* Task Content */}
                        <div className="space-y-6">
                          {activeTask.content?.filter((item) => {
                            // End screens only show content items, not form fields
                            if (activeTask.type === 'end' && item.category === 'form') return false;
                            return true;
                          }).map((item) => (
                            <div key={item.id}>
                              {item.category === 'form'
                                ? renderFormField(item)
                                : renderContentItem(item)
                              }
                            </div>
                          ))}

                          {(!activeTask.content || activeTask.content.length === 0) && (
                            <div className="text-center py-8 text-muted-foreground">
                              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                              <p>No content added to this task yet</p>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons for End Screens */}
                        {activeTask.actionButtons?.length > 0 && (
                          <div className="mt-6 space-y-3">
                            {activeTask.actionButtons.map((btn) => {
                              const mergedData = { ...previewFormData, ...flowOutputData };
                              const resolvedUrl = replaceVariablesInContent(btn.url || '', mergedData, allFields)
                                .replace(/<[^>]+>/g, '');

                              console.log('[ActionBtn] btn.url:', btn.url, '| resolvedUrl:', resolvedUrl, '| flowOutputData keys:', Object.keys(flowOutputData));
                              const isExternalUrl = resolvedUrl && /^https?:\/\//.test(resolvedUrl);
                              const ButtonIcon = btn.icon === 'download' ? Download
                                : btn.icon === 'external' ? ExternalLink
                                : null;

                              return isExternalUrl ? (
                                <a
                                  key={btn.id}
                                  href={resolvedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`inline-flex items-center justify-center w-full h-11 rounded-md text-sm font-medium transition-colors ${
                                    btn.style === 'outline'
                                      ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                  }`}
                                >
                                  {ButtonIcon && <ButtonIcon className="h-4 w-4 mr-2" />}
                                  {btn.label || 'Action'}
                                </a>
                              ) : (
                                <Button
                                  key={btn.id}
                                  variant={btn.style === 'outline' ? 'outline' : 'default'}
                                  size="lg"
                                  className={`w-full ${btn.style !== 'outline' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                                  disabled
                                >
                                  {ButtonIcon && <ButtonIcon className="h-4 w-4 mr-2" />}
                                  {btn.label || 'Action'}
                                </Button>
                              );
                            })}
                          </div>
                        )}

                        {/* Close Button for End Screens */}
                        {activeTask.type === 'end' && (
                          <div className="mt-8 pt-6 border-t">
                            <Button
                              className="w-full bg-emerald-600 hover:bg-emerald-700"
                              size="lg"
                              onClick={() => toast.success('Preview completed! In a real run, this would close the workflow.')}
                            >
                              <FlagTriangleRight className="h-4 w-4 mr-2" />
                              Done
                            </Button>
                          </div>
                        )}

                        {/* Complete Button - Preview mode (not for end screens) */}
                        {activeTask.type !== 'end' && (
                          <div className="mt-8 pt-6 border-t">
                            <Button
                              className="w-full bg-green-600 hover:bg-green-700"
                              size="lg"
                              onClick={() => {
                                if (activeTaskIndex < tasks.length - 1) {
                                  navigateToTask(activeTaskIndex + 1);
                                } else {
                                  toast.success('Preview completed! In a real run, this would complete the workflow.');
                                }
                              }}
                            >
                              <Check className="h-4 w-4 mr-2" />
                              {activeTaskIndex < tasks.length - 1 ? 'Complete Task' : 'Complete Workflow'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                      Select a task to view
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Mobile Preview */
            <div className="flex flex-col items-center py-4">
              {/* Phone Frame */}
              <div className="relative">
                {/* Phone bezel */}
                <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  {/* Phone notch */}
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-full z-10" />

                  {/* Phone screen */}
                  <div className="bg-white rounded-[2.25rem] w-[375px] h-[700px] overflow-hidden relative">
                    {/* Status bar - iOS style */}
                    <div className="bg-gray-100 px-6 py-2 flex items-center justify-between text-xs font-medium">
                      <span>{formatTime(currentTime)}</span>
                      <div className="flex items-center gap-1.5">
                        {/* Signal bars */}
                        <div className="flex items-end gap-[2px]">
                          <div className="w-[3px] h-[4px] bg-gray-800 rounded-[1px]" />
                          <div className="w-[3px] h-[6px] bg-gray-800 rounded-[1px]" />
                          <div className="w-[3px] h-[8px] bg-gray-800 rounded-[1px]" />
                          <div className="w-[3px] h-[10px] bg-gray-800 rounded-[1px]" />
                        </div>
                        {/* WiFi icon */}
                        <svg className="w-4 h-3" viewBox="0 0 16 12" fill="currentColor">
                          <path d="M8 9.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM4.5 7.5c.4-.4 1-.7 1.6-.9.6-.2 1.2-.3 1.9-.3s1.3.1 1.9.3c.6.2 1.2.5 1.6.9l1.1-1.1c-.6-.6-1.3-1-2.1-1.3-.8-.3-1.6-.4-2.5-.4s-1.7.1-2.5.4c-.8.3-1.5.7-2.1 1.3l1.1 1.1zM1.5 4.5c.9-.9 2-1.6 3.2-2.1 1.2-.5 2.5-.7 3.8-.7s2.6.2 3.8.7c1.2.5 2.3 1.2 3.2 2.1l1.1-1.1C15.4 2.2 14 1.3 12.5.7 11 .2 9.5 0 8 0S5 .2 3.5.7C2 1.3.6 2.2-.5 3.4l1.1 1.1z" />
                        </svg>
                        {/* Battery */}
                        <div className="flex items-center">
                          <div className="w-[22px] h-[11px] border-[1.5px] border-gray-800 rounded-[3px] flex items-center p-[1px]">
                            <div className="w-[14px] h-[6px] bg-green-500 rounded-[1.5px]" />
                          </div>
                          <div className="w-[2px] h-[5px] bg-gray-800 rounded-r-[1px] -ml-[0.5px]" />
                        </div>
                      </div>
                    </div>

                    {/* Mobile content */}
                    <div className="h-[calc(100%-32px)] overflow-y-auto">
                      {/* Progress indicator */}
                      <div className="px-4 py-3 border-b bg-gray-50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">
                            Task {activeTaskIndex + 1} of {tasks.length}
                          </span>
                          <span className="text-xs font-medium">{Math.round(progressPercent)}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-1" />
                      </div>

                      {/* Task content area */}
                      <div
                        className={`min-h-[500px] ${
                          !workflow.settings?.background
                            ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200'
                            : ''
                        }`}
                        style={
                          workflow.settings?.background
                            ? workflow.settings.background.type === 'image'
                              ? { backgroundImage: `url(${workflow.settings.background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                              : { background: workflow.settings.background.value }
                            : {}
                        }
                      >
                        {/* Mobile Flow Execution Screen */}
                        {isFlowExecuting ? (
                          <div className="p-4">
                            <div className="bg-white rounded-lg shadow-lg p-5 min-h-[350px] flex flex-col items-center justify-center">
                              <FlowExecutionScreen
                                compact
                                isComplete={!!flowResult && !flowResult.error}
                                error={flowResult?.error || null}
                                customMessage={flowExecutionMessage !== 'Running automation...' ? flowExecutionMessage : null}
                                onRetry={() => {
                                  setFlowResult(null);
                                  flowExecutionRef.current = null;
                                  setIsFlowExecuting(false);
                                  setActiveTaskIndex(() => {
                                    setTimeout(() => setActiveTaskIndex(activeTaskIndex), 0);
                                    return -1;
                                  });
                                }}
                                onSkip={() => {
                                  if (activeTaskIndex < tasks.length - 1) navigateToTask(activeTaskIndex + 1);
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                        <div className="p-4">
                          <div className="bg-white rounded-lg shadow-lg p-5">
                            {/* End Screen Badge - Mobile */}
                            {activeTask?.type === 'end' && (
                              <div className="flex items-center gap-2 mb-2">
                                <div className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-medium flex items-center gap-1">
                                  <FlagTriangleRight className="h-2.5 w-2.5" />
                                  End Screen
                                </div>
                              </div>
                            )}

                            {/* Task Header */}
                            <h2 className="text-lg font-bold mb-4">
                              {activeTask?.formTitle || activeTask?.name || 'Untitled Task'}
                            </h2>

                            <div className={`border-t mb-4 ${activeTask?.type === 'end' ? 'border-emerald-200' : ''}`} />

                            {/* Task Content */}
                            <div className="space-y-4">
                              {activeTask?.content?.filter((item) => {
                                if (activeTask?.type === 'end' && item.category === 'form') return false;
                                return true;
                              }).map((item) => (
                                <div key={item.id} className="text-sm">
                                  {item.category === 'form'
                                    ? renderFormField(item)
                                    : renderContentItem(item)
                                  }
                                </div>
                              ))}

                              {(!activeTask?.content || activeTask.content.length === 0) && (
                                <div className="text-center py-6 text-muted-foreground">
                                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                  <p className="text-sm">No content added to this task yet</p>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons for End Screens - Mobile */}
                            {activeTask?.type === 'end' && activeTask.actionButtons?.length > 0 && (
                              <div className="mt-4 space-y-2">
                                {activeTask.actionButtons.map((btn) => {
                                  const resolvedUrl = replaceVariablesInContent(btn.url || '', { ...previewFormData, ...flowOutputData }, allFields)
                                    .replace(/<[^>]+>/g, '');
                                  const isExternalUrl = resolvedUrl && /^https?:\/\//.test(resolvedUrl);
                                  const ButtonIcon = btn.icon === 'download' ? Download
                                    : btn.icon === 'external' ? ExternalLink
                                    : null;
                                  return isExternalUrl ? (
                                    <a
                                      key={btn.id}
                                      href={resolvedUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`inline-flex items-center justify-center w-full h-10 rounded-md text-sm font-medium transition-colors ${
                                        btn.style === 'outline'
                                          ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                      }`}
                                    >
                                      {ButtonIcon && <ButtonIcon className="h-3 w-3 mr-1.5" />}
                                      {btn.label || 'Action'}
                                    </a>
                                  ) : (
                                    <Button
                                      key={btn.id}
                                      variant={btn.style === 'outline' ? 'outline' : 'default'}
                                      size="default"
                                      className={`w-full text-sm ${btn.style !== 'outline' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                                      disabled
                                    >
                                      {ButtonIcon && <ButtonIcon className="h-3 w-3 mr-1.5" />}
                                      {btn.label || 'Action'}
                                    </Button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Close Button for End Screens - Mobile */}
                            {activeTask?.type === 'end' && (
                              <div className="mt-6 pt-4 border-t">
                                <Button
                                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                                  size="default"
                                  onClick={() => toast.success('Preview completed!')}
                                >
                                  <FlagTriangleRight className="h-4 w-4 mr-2" />
                                  Done
                                </Button>
                              </div>
                            )}

                            {/* Complete Button - Mobile (not for end screens) */}
                            {activeTask?.type !== 'end' && (
                              <div className="mt-6 pt-4 border-t">
                                <Button
                                  className="w-full bg-green-600 hover:bg-green-700"
                                  size="default"
                                  onClick={() => {
                                    if (activeTaskIndex < tasks.length - 1) {
                                      navigateToTask(activeTaskIndex + 1);
                                    } else {
                                      toast.success('Preview completed!');
                                    }
                                  }}
                                >
                                  <Check className="h-4 w-4 mr-2" />
                                  {activeTaskIndex < tasks.length - 1 ? 'Complete Task' : 'Complete Workflow'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Navigation - below phone */}
              <div className="mt-6 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateToTask(Math.max(0, activeTaskIndex - 1))}
                  disabled={activeTaskIndex === 0}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex gap-1">
                  {tasks.map((task, index) => {
                    const isFlow = task.type === 'flow';
                    const isEnd = task.type === 'end';
                    return (
                      <button
                        key={index}
                        onClick={() => navigateToTask(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === activeTaskIndex
                            ? isFlow ? 'bg-blue-500' : isEnd ? 'bg-emerald-500' : 'bg-primary'
                            : index < activeTaskIndex
                            ? isFlow ? 'bg-blue-400' : isEnd ? 'bg-emerald-400' : 'bg-green-500'
                            : 'bg-gray-300'
                        }`}
                      />
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateToTask(Math.min(tasks.length - 1, activeTaskIndex + 1))}
                  disabled={activeTaskIndex === tasks.length - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </PageTransition>
    </AppShell3>
  );
}
